<template>
  <Teleport :to="`#${target}`" :disabled="!target || isLargeLayout" defer>
    <div class="CartSubscription-wrapper">
      <article
        ref="root"
        class="CartSubscription u-container"
        :class="{
          'CartSubscription--collapsible': mode === 'collapsible',
          'CartSubscription--extended': mode === 'extended',
        }"
        v-bind="$attrs"
      >
        <CollapsibleRoot
          v-model:open="isOpen"
          :disabled="isExtendedMode"
          class="CartSubscription-summary"
        >
          <CollapsibleContent class="CartSubscription-contentSection">
            <div class="CartSubscription-contentScroller">
              <!-- The whole header closes the cart; the chevron below just bubbles into it. -->
              <CollapsibleTrigger as="div" class="CartSubscription-headingSection">
                <h5 class="CartSubscription-headingTitle">
                  <Icon icon="cart" size="md" />
                  {{ heading }}
                </h5>

                <IconButton
                  v-if="mode === 'collapsible'"
                  icon="chevron-down"
                  size="lg"
                  class="CartSubscription-closeButton"
                  :ariaLabel="t('ui.a11y.close')"
                />
              </CollapsibleTrigger>

              <slot name="sections" />

              <div v-if="items.length" class="CartSubscription-itemsSection">
                <CartSubscriptionItem
                  v-for="item of items"
                  :key="item.name"
                  :label="item.name"
                  :value="item.value"
                  :originalValue="item.originalValue"
                  :tag="item.promo ? promoTagLabel : undefined"
                  :description="item.description"
                  :quantity="item.quantity"
                />
              </div>

              <div v-if="$slots.append && !isLargeLayout" class="CartSubscription-appendSection">
                <slot name="append" />
              </div>

              <div v-if="isRecurringTotalVisible && mode === 'collapsible'" class="CartSubscription-recurringTotalSection CartSubscription-recurringTotalSection--inside">
                <CartSubscriptionItem
                  :label="recurringTotalText!"
                  :value="recurringTotal!"
                />
              </div>

              <div v-if="$slots.disclaimer" class="CartSubscription-disclaimerSection is-inside">
                <slot name="disclaimer" />
              </div>
            </div>
          </CollapsibleContent>

          <CollapsibleTrigger class="CartSubscription-totalSection">
            <div class="CartSubscription-totalRow">
              <span class="CartSubscription-totalText">
                <IconButton
                  v-if="mode === 'collapsible'"
                  :icon="isOpen ? 'chevron-down' : 'chevron-up'"
                  size="lg"
                  class="CartSubscription-toggleButton"
                  :ariaLabel="t('ui.components.accordion.toggleAriaLabel')"
                />
                {{ totalText }}
              </span>

              <span class="CartSubscription-totalPrice">
                {{ total }}

                <ActionButton
                  v-if="totalAction && !isLargeLayout"
                  v-show="!isOpen"
                  class="CartSubscription-totalAction"
                  size="sm"
                  :disabled="totalActionDisabled"
                  @click.stop="clickTotalAction"
                >
                  {{ totalActionLabel }}
                </ActionButton>
              </span>
            </div>

            <div v-if="totalDescription || savingsText" class="CartSubscription-totalMeta">
              <span v-if="totalDescription" class="CartSubscription-totalDescription">{{ totalDescription }}</span>

              <Tag
                v-if="savingsText"
                type="success"
                variant="filled"
                size="xs"
                class="CartSubscription-savingsTag"
              >
                {{ savingsText }}
              </Tag>
            </div>
          </CollapsibleTrigger>

          <div v-if="isRecurringTotalVisible" class="CartSubscription-recurringTotalSection CartSubscription-recurringTotalSection--outside">
            <CartSubscriptionItem
              :label="recurringTotalText!"
              :value="recurringTotal!"
            />
          </div>

          <div v-if="futureBillings" v-show="isOpen" class="CartSubscription-futureBillingsSection">
            <CartSubscriptionItem
              :label="futureBillings.label"
              :description="futureBillings.description"
              :value="futureBillings.price"
              :originalValue="futureBillings.originalValue"
            />
          </div>
        </CollapsibleRoot>

        <ActionButton
          v-if="totalAction && !isLargeLayout"
          v-show="isOpen"
          class="CartSubscription-totalAction"
          size="sm"
          :disabled="totalActionDisabled"
          @click="clickTotalAction"
        >
          {{ totalActionLabel }}
        </ActionButton>

        <div v-if="$slots.belowCart" v-show="isOpen" class="CartSubscription-belowCartSection">
          <slot name="belowCart" />
        </div>
      </article>

      <div v-if="$slots.disclaimer" class="CartSubscription-disclaimerSection is-outside">
        <slot name="disclaimer" />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, Teleport, watch, useTemplateRef } from 'vue';
import { useScrollLock } from '@vueuse/core';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { useResponsive } from '@cvo/ui/composables';
import { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent } from 'reka-ui';
import Icon from '../../atoms/Icon/Icon.vue';
import IconButton from '../../atoms/IconButton/IconButton.vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import type { CartSubscriptionProps } from './CartSubscription.model';
import CartSubscriptionItem from './CartSubscriptionItem.vue';

const props = withDefaults(defineProps<CartSubscriptionProps>(), {
  items: () => [],
  target: 'bottom-sticky',
  mode: 'collapsible',
  totalActionDisabled: false,
});

const emit = defineEmits<{
  (e: 'click:total-action'): void;
}>();

defineOptions({ inheritAttrs: false });

const { t } = useI18n();

// Responsive and collapsible logic to use scss customizable breakpoint value
const root = useTemplateRef<HTMLElement | null>('root');
const { isDesktop: isLargeLayout } = useResponsive({
  element: root,
  customProperty: '--cart-breakpoint',
});

// The "Future billings" row supersedes the recurring-total row (both describe upcoming billings).
const isRecurringTotalVisible = computed(() => {
  return !props.futureBillings
    && props.recurringTotal
    && props.recurringTotalText
    && props.recurringTotal !== props.total;
});

const isExtendedMode = computed(() => props.mode === 'extended' || isLargeLayout.value);
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const isOpen = ref(isExtendedMode.value);

watch(isExtendedMode, (newIsExtended) => {
  isOpen.value = newIsExtended;
});

function clickTotalAction() {
  emit('click:total-action');
}

const isScrollLocked = useScrollLock(document.body);

watch([isOpen, isLargeLayout], ([open, isLarge]) => {
  if(props.mode === 'extended') {
    isScrollLocked.value = false;
    return;
  }

  isScrollLocked.value = open && !isLarge;
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/mixins';
@use '@cvo/ui/styles/utils/responsive';

// The wrapper animates height only; its scroller animates opacity/offset, so the two
// never fight each other (a transform on the animated box shears the reveal).
@keyframes cartSummarySlideDown {
  from { height: 0; }
  to { height: var(--reka-collapsible-content-height); }
}

@keyframes cartSummarySlideUp {
  from { height: var(--reka-collapsible-content-height); }
  to { height: 0; }
}

@keyframes cartSummaryContentIn {
  from {
    opacity: 0;
    transform: translateY(-#{vars.$dimensions-d-2});
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cartSummaryContentOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-#{vars.$dimensions-d-2});
  }
}

@layer cvo-ui {
  // ===================
  // Base Component
  // ===================

  .CartSubscription {
    @include mixins.card-bordered(
      $bg: vars.$organisms-cart-cart-bg-color,
      $border: vars.$organisms-cart-cart-border-stroke solid vars.$organisms-cart-cart-border-color,
      $radius: vars.$organisms-cart-cart-lg-md-border-radius,
    );

    --cart-breakpoint: #{vars.$cart-breakpoint};
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-4;
    width: 100%;
    padding: vars.$organisms-cart-cart-lg-md-padding-top-bottom vars.$organisms-cart-cart-lg-md-padding-left-right;

    &:not(.CartSubscription--extended) {
      @include responsive.tablet-down {
        max-height: calc(100vh - vars.$header-min-height-desktop);
        display: flex;
        flex-direction: column;
      }

      @include responsive.mobile {
        max-height: calc(100vh - vars.$header-min-height-mobile);
      }
    }

    &-wrapper {
      width: 335px;
    }

    // Modifier: Collapsible mode
    &--collapsible {
      @media screen and (max-width: vars.$cart-breakpoint) {
        @include mixins.card-elevated(
          $bg: vars.$organisms-cart-cart-bg-color,
          $radius: vars.$organisms-cart-cart-sm-border-radius vars.$organisms-cart-cart-sm-border-radius 0 0,
        );

        // Collapsed: only the total row is visible (the content above it has zero height),
        // so its top border/padding are dropped; both fade in/out in sync with the content animation below.
        .CartSubscription-totalSection {
          cursor: pointer;
          user-select: none; // The bar is a tap target; avoid selecting the label on tap
          padding-top: 0;
          border-top: vars.$organisms-cart-cart-border-stroke solid transparent;
          transition:
            padding-top vars.$transition-base cubic-bezier(0.32, 0.72, 0, 1),
            border-color vars.$transition-base cubic-bezier(0.32, 0.72, 0, 1);

          &[data-state='open'] {
            padding-top: vars.$dimensions-d-4;
            border-top-color: vars.$organisms-cart-cart-border-color;
          }
        }

        .CartSubscription-recurringTotalSection {
          &--inside {
            display: revert;
          }
          &--outside {
            display: none;
          }
        }

        .CartSubscription-promoCodeSection {
          display: none;
        }
      }
    }

    // The heading's bottom border already separates whichever section comes first.
    &-headingSection + * {
      border-top: none !important;
    }

    &-summary {
      display: flex;
      flex-direction: column;
    }

    &-contentScroller {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    // ===================
    // Heading Section
    // ===================
    &-headingSection {
      color: vars.$organisms-cart-cart-title-color;
      border-bottom: vars.$organisms-cart-cart-border-stroke solid vars.$organisms-cart-cart-border-color;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: vars.$dimensions-d-2;
      padding-bottom: vars.$dimensions-d-2;
    }

    &-headingTitle {
      @include text.heading(h5);

      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      font-weight: vars.$typography-weight-heading;

      .Icon {
        color: vars.$organisms-cart-cart-icon-color;
      }
    }

    // ===================
    // Items Section
    // ===================
    &-itemsSection {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-4;
      padding: vars.$dimensions-d-4 0;
      border-top: vars.$organisms-cart-cart-border-stroke solid vars.$organisms-cart-cart-border-color;
    }

    // ===================
    // Total Section
    // ===================
    &-totalSection {
      text-align: start;
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
      padding-top: vars.$dimensions-d-4;
      border-top: vars.$organisms-cart-cart-border-stroke solid vars.$organisms-cart-cart-border-color;
    }

    &-totalRow {
      display: flex;
      justify-content: space-between;
      gap: vars.$dimensions-d-4;
      min-height: 32px; // Avoid height shift when "totalAction" button appears
    }

    &-totalText,
    &-totalPrice {
      color: vars.$organisms-cart-cart-primary-text-color;
      @include text.text(lg);

      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      font-weight: vars.$typography-weight-strong;

      .IconButton {
        color: vars.$color-neutral-gray-700;
      }
    }

    &-totalPrice {
      text-align: end;
    }

    & &-toggleButton,
    & &-closeButton {
      @media screen and (min-width: (vars.$cart-breakpoint + 1)) {
        display: none;
      }
    }

    &-totalMeta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: vars.$dimensions-d-2;
    }

    &-totalDescription {
      color: vars.$organisms-cart-cart-secondary-text-color;
      @include text.text(sm);
    }

    &-savingsTag {
      margin-inline-start: auto;
    }

    // ===================
    // Recurring Total Section
    // ===================
    &-recurringTotalSection {
      margin-top: vars.$dimensions-d-4;
      padding-top: vars.$dimensions-d-4;
      border-top: vars.$organisms-cart-cart-border-stroke solid vars.$organisms-cart-cart-border-color;

      &--inside {
        display: none;
      }

      .CartSubscriptionItem-label,
      .CartSubscriptionItem-value {
        color: vars.$organisms-cart-cart-secondary-text-color;
        @include text.text(md);
      }
    }

    // ===================
    // Future billings section
    // ===================
    &-futureBillingsSection {
      margin-top: vars.$dimensions-d-4;
      padding-top: vars.$dimensions-d-4;
      border-top: vars.$organisms-cart-cart-border-stroke solid vars.$organisms-cart-cart-border-color;

      .CartSubscriptionItem-label {
        color: vars.$organisms-cart-cart-secondary-text-color;
        @include text.text(md);
      }

      .CartSubscriptionItem-value {
        @include text.text(md);
      }
    }

    // ===================
    // Quota breakdown section
    // ===================
    &-belowCartSection {
      margin-top: vars.$dimensions-d-4;
    }

    // ===================
    // Disclaimer section
    // ===================
    &-disclaimerSection {
      color: vars.$organisms-cart-cart-disclaimer-color;
      @include text.text(sm);
      @include responsive.desktop {
        margin-top: vars.$dimensions-d-3;
      }

      &.is-inside {
        display: none;
      }
    }
  }

  // ===================
  // Mobile Responsive
  // ===================
  @media screen and (max-width: vars.$cart-breakpoint) {
    .CartSubscription {
      border-radius: vars.$organisms-cart-cart-sm-border-radius;
      padding: vars.$organisms-cart-cart-sm-padding-top-bottom vars.$organisms-cart-cart-sm-padding-left-right;

      &-wrapper {
        width: 100%;
      }

      // Animated box: height only. Scrolling lives on the inner scroller so scrollbars
      // don't flash while the height animates.
      &-contentSection {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: var(--section-anim) var(--anim-duration) var(--anim-easing);

        > .CartSubscription-contentScroller {
          animation: var(--content-anim) var(--anim-duration) var(--anim-easing);
        }

        &[data-state='open'] {
          --section-anim: cartSummarySlideDown;
          --content-anim: cartSummaryContentIn;
          --anim-duration: #{vars.$transition-slow};
          --anim-easing: cubic-bezier(0.32, 0.72, 0, 1);
        }

        &[data-state='closed'] {
          --section-anim: cartSummarySlideUp;
          --content-anim: cartSummaryContentOut;
          --anim-duration: #{vars.$transition-base};
          --anim-easing: cubic-bezier(0.7, 0, 0.84, 0);
        }

        @media (prefers-reduced-motion: reduce) {
          animation: none;

          > .CartSubscription-contentScroller {
            animation: none;
          }
        }
      }

      // Shrinks to whatever the card's max-height leaves, so the overflow scrolls
      // instead of being clipped by the animated box above.
      &-contentScroller {
        overflow-y: auto;
      }

      // Stays put while the sheet's body scrolls under it.
      &-headingSection {
        cursor: pointer;
        user-select: none; // The header is a tap target; avoid selecting the title on tap
        position: sticky;
        inset-block-start: 0;
        z-index: 1;
        background-color: vars.$organisms-cart-cart-bg-color;
        padding-block: vars.$dimensions-d-2 vars.$dimensions-d-4;
      }

      &-summary {
        min-height: 0;
      }

      &-totalAction {
        flex-shrink: 0;
      }

      &-recurringTotalSection {
        padding-bottom: vars.$dimensions-d-4;
      }

      &-disclaimerSection {
        background-color: vars.$organisms-cart-cart-bg-color;
        border-top: vars.$organisms-cart-cart-border-stroke solid vars.$organisms-cart-cart-border-color;
        padding: vars.$organisms-cart-cart-sm-padding-top-bottom 0;

        &.is-outside {
          display: none;
        }
        &.is-inside {
          display: revert;
        }
      }
    }
  }
}
</style>
